import React from "react";
import { useTranslation } from "react-i18next";

export default function QuestionItem({
  question,
  index,
  onUpdate,
  onCopy,
  onDelete,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
  themeStyles,
}) {
  const { t } = useTranslation();

  const questionTypes = [
    { value: "short_answer", label: t("common.survey.shortAnswer") },
    { value: "long_answer", label: t("common.survey.longAnswer") },
    { value: "multiple_choice", label: t("common.survey.multipleChoice") },
    { value: "checkbox", label: t("common.survey.checkbox") },
  ];

  const handleTypeChange = (newType) => {
    onUpdate(question.id, "question_type", newType);
  };

  // 설명 체크박스 상태 확인 (null이 아니고 빈 문자열도 아닌 경우 true)
  const hasDescription =
    question.question_description !== null &&
    question.question_description !== undefined;

  const handleDescriptionToggle = (checked) => {
    if (checked) {
      // 체크하면 빈 문자열로 설정하여 textarea 표시
      onUpdate(question.id, "question_description", "");
    } else {
      // 체크 해제하면 null로 설정하여 textarea 숨김
      onUpdate(question.id, "question_description", null);
    }
  };

  // 선택이유 체크박스 상태 확인
  const hasExtraReason = question.extra_reason === true;

  const handleExtraReasonToggle = (checked) => {
    onUpdate(question.id, "extra_reason", checked);
  };

  // 숫자만 체크박스 상태 확인 (단답형일 때만)
  const isNumberOnly = question.input_type === "number";

  const handleNumberOnlyToggle = (checked) => {
    if (checked) {
      // 체크하면 input_type을 number로 설정
      onUpdate(question.id, "input_type", "number");
    } else {
      // 체크 해제하면 input_type을 text로 설정
      onUpdate(question.id, "input_type", "text");
    }
  };

  // 활성/비활성 상태 확인 (기본값은 true)
  const isActive = question.is_active !== false;

  const handleActiveToggle = () => {
    onUpdate(question.id, "is_active", !isActive);
  };

  return (
    <div
      className={`${themeStyles.questionCard} ${
        !isActive ? themeStyles.inactiveQuestion : ""
      }`}
    >
      <div className={themeStyles.questionContent}>
        {/* 질문 텍스트 */}
        <input
          type="text"
          className={themeStyles.questionInput}
          placeholder={t("common.survey.questionPlaceholder")}
          value={question.question_text}
          onChange={(e) =>
            onUpdate(question.id, "question_text", e.target.value)
          }
        />
        {/* 질문 설명 (체크박스가 선택된 경우에만 표시) */}
        {hasDescription && (
          <textarea
            className={themeStyles.descriptionInput}
            placeholder={t("common.survey.descriptionPlaceholder")}
            value={question.question_description || ""}
            onChange={(e) =>
              onUpdate(question.id, "question_description", e.target.value)
            }
          />
        )}

        {/* 질문 타입 및 옵션 */}
        <div className="d-flex justify-content-between">
          <div>
            <div className={themeStyles.optionRow}>
              <select
                className={themeStyles.typeSelect}
                value={question.question_type}
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                {questionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <label className={themeStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={question.is_required}
                  onChange={(e) =>
                    onUpdate(question.id, "is_required", e.target.checked)
                  }
                />
                {t("common.survey.required")}
              </label>

              <label className={themeStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={question.reflect_in_document}
                  onChange={(e) =>
                    onUpdate(
                      question.id,
                      "reflect_in_document",
                      e.target.checked
                    )
                  }
                />
                {t("common.survey.documentReflect")}
              </label>

              <label className={themeStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={hasDescription}
                  onChange={(e) => handleDescriptionToggle(e.target.checked)}
                />
                {t("common.survey.description")}
              </label>

              {/* 단답형일 때만 숫자만 체크박스 표시 */}
              {question.question_type === "short_answer" && (
                <label className={themeStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isNumberOnly}
                    onChange={(e) => handleNumberOnlyToggle(e.target.checked)}
                  />
                  {t("common.survey.numberOnly")}
                </label>
              )}

              {/* 객관식/체크박스일 때만 선택이유 체크박스 표시 */}
              {(question.question_type === "multiple_choice" ||
                question.question_type === "checkbox") && (
                <label className={themeStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={hasExtraReason}
                    onChange={(e) => handleExtraReasonToggle(e.target.checked)}
                  />
                  {t("common.survey.reasonForChoice")}
                </label>
              )}
            </div>
          </div>

          <div>
            <button
              className={themeStyles.copyButton}
              onClick={() => onCopy(question.id)}
            >
              {t("common.survey.copy")}
            </button>
            <button
              className={themeStyles.deleteButton}
              onClick={() => onDelete(question.id)}
            >
              {t("common.survey.delete")}
            </button>
            <button
              className={`${themeStyles.toggleButton} ${
                isActive ? themeStyles.activeButton : themeStyles.inactiveButton
              }`}
              onClick={handleActiveToggle}
              title={isActive ? "질문을 비활성화합니다" : "질문을 활성화합니다"}
            >
              {isActive ? "비활성" : "활성"}
            </button>
          </div>
        </div>
        {/* 객관식/체크박스 옵션 */}
        {(question.question_type === "multiple_choice" ||
          question.question_type === "checkbox") && (
          <div className={themeStyles.optionsSection}>
            <h6 className={themeStyles.optionsTitle}>
              {t("common.survey.options")}
            </h6>
            {question.options.map((option, optIndex) => (
              <div key={option.id} className={themeStyles.optionContainer}>
                <div className={themeStyles.optionRow}>
                  <span className={themeStyles.optionIcon}>
                    {question.question_type === "multiple_choice" ? "○" : "☐"}
                  </span>
                  <input
                    type="text"
                    className={themeStyles.optionInput}
                    placeholder={`${t("common.survey.optionPlaceholder")} ${
                      optIndex + 1
                    }`}
                    value={option.option_text}
                    onChange={(e) =>
                      onUpdateOption(question.id, option.id, e.target.value)
                    }
                  />
                  {/* 선택이유가 활성화된 경우 이유 입력란 표시 */}
                  {hasExtraReason && (
                    <input
                      type="text"
                      className={themeStyles.reasonInput}
                      placeholder={t("common.survey.reasonPlaceholder")}
                      disabled
                      style={{
                        backgroundColor: "#f8f9fa",
                        color: "#6c757d",
                        fontStyle: "italic",
                      }}
                    />
                  )}
                  <button
                    className={themeStyles.removeOptionButton}
                    onClick={() => onDeleteOption(question.id, option.id)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
            <button
              className={themeStyles.addOptionButton}
              onClick={() => onAddOption(question.id)}
            >
              + {t("common.survey.addOption")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
